import { API_CACHE_TTL, fetchCachedApi, fetchCachedGetApi } from "./apiCache";
import { TITLE_MATCH_THRESHOLD, scoreTitleMatch } from "./titleMatch";

export type AppleMusicLookupType = "song" | "album" | "artist";
export type AppleMusicEnrichmentSource = "apple-music" | "itunes";

export interface AppleMusicResource {
  id: string;
  type: string;
  attributes?: {
    name?: string;
    artistName?: string;
    albumName?: string;
    url?: string;
    artwork?: {
      url?: string;
    };
    previews?: Array<{ url?: string }>;
    releaseDate?: string;
  };
}

export interface AppleMusicSearchResponse {
  error?: string;
  results?: {
    songs?: { data?: AppleMusicResource[] };
    albums?: { data?: AppleMusicResource[] };
    artists?: { data?: AppleMusicResource[] };
  };
}

export interface ItunesTrack {
  artistId?: number;
  trackId?: number;
  collectionId?: number;
  wrapperType?: string;
  kind?: string;
  trackName?: string;
  artistName?: string;
  collectionName?: string;
  artworkUrl?: string;
  previewUrl?: string;
  artistLinkUrl?: string;
  trackViewUrl?: string;
  collectionViewUrl?: string;
  releaseDate?: string;
  primaryGenreName?: string;
  trackTimeMillis?: number;
}

export interface ItunesData {
  resultCount?: number;
  tracks?: ItunesTrack[];
}

export interface AppleMusicEnrichmentQuery {
  artistName?: string | null;
  releaseName?: string | null;
  albumName?: string | null;
  lookupType: AppleMusicLookupType;
  spotifyId?: string | null;
}

export interface AppleMusicEnrichment {
  key: string;
  source: AppleMusicEnrichmentSource;
  type: AppleMusicLookupType;
  url: string;
  name?: string;
  artistName?: string;
  albumName?: string;
  artworkUrl?: string;
  previewUrl?: string;
  releaseDate?: string;
  resourceId?: string;
  raw?: AppleMusicResource | ItunesTrack;
}

export interface FetchAppleMusicEnrichmentOptions {
  signal?: AbortSignal;
  requirePreview?: boolean;
  ttlMs?: number;
  appleLimit?: number;
  itunesLimit?: number;
}

export const normalizeAppleMusicKey = (value = "") => value.toLowerCase().replace(/[^a-z0-9]/g, "");

export const getAppleMusicEnrichmentKey = (query: AppleMusicEnrichmentQuery) => {
  return [
    query.lookupType,
    normalizeAppleMusicKey(query.artistName || ""),
    normalizeAppleMusicKey(query.releaseName || query.albumName || ""),
    normalizeAppleMusicKey(query.albumName || ""),
    query.spotifyId || "",
  ].join(":");
};

export const hasAppleMusicResults = (data?: AppleMusicSearchResponse) =>
  Boolean(
    data?.results &&
      !data.error &&
      ((data.results.songs?.data?.length || 0) +
        (data.results.albums?.data?.length || 0) +
        (data.results.artists?.data?.length || 0) >
        0)
  );

export const hasItunesResults = (data?: ItunesData) => Boolean(Array.isArray(data?.tracks) && data.tracks.length > 0);

const getAppleResources = (data: AppleMusicSearchResponse | undefined, lookupType: AppleMusicLookupType) => {
  if (lookupType === "artist") return data?.results?.artists?.data || [];
  if (lookupType === "album") return data?.results?.albums?.data || [];
  return data?.results?.songs?.data || [];
};

const isArtistMatch = (candidateArtist = "", artistName = "") => {
  const candidate = normalizeAppleMusicKey(candidateArtist);
  const expected = normalizeAppleMusicKey(artistName);
  return Boolean(candidate && expected && (candidate === expected || candidate.includes(expected) || expected.includes(candidate)));
};

const secondaryTitleBonus = (target: string, candidate?: string) => {
  const score = scoreTitleMatch(target, candidate);
  return score >= TITLE_MATCH_THRESHOLD ? Math.round(40 * score) : 0;
};

const releaseTitleScore = (target: string, primary?: string, secondary?: string) => {
  const primaryScore = scoreTitleMatch(target, primary);
  if (primaryScore < TITLE_MATCH_THRESHOLD) return 0;
  return Math.round(120 * primaryScore) + secondaryTitleBonus(target, secondary);
};

const appleReleaseNameScore = (resource: AppleMusicResource, query: AppleMusicEnrichmentQuery) => {
  const target = query.releaseName || query.albumName || "";
  return releaseTitleScore(target, resource.attributes?.name, resource.attributes?.albumName);
};

const appleNameScore = (resource: AppleMusicResource, query: AppleMusicEnrichmentQuery) => {
  const artistName = normalizeAppleMusicKey(resource.attributes?.artistName || "");
  let score = query.lookupType === "artist" ? 0 : appleReleaseNameScore(resource, query);

  if (query.artistName && isArtistMatch(resource.attributes?.artistName, query.artistName)) score += 70;
  if (query.lookupType === "artist" && artistName === normalizeAppleMusicKey(query.artistName || "")) score += 120;
  if (resource.attributes?.url) score += 12;

  return score;
};

export const selectAppleMusicResource = (
  data: AppleMusicSearchResponse | undefined,
  query: AppleMusicEnrichmentQuery
): AppleMusicResource | null => {
  const resources = getAppleResources(data, query.lookupType);
  if (resources.length === 0) return null;

  const matched = resources
    .filter((resource) => {
      if (!resource.attributes?.url) return false;
      if (query.lookupType === "artist") return true;
      if (appleReleaseNameScore(resource, query) <= 0) return false;
      return !query.artistName || isArtistMatch(resource.attributes?.artistName, query.artistName);
    })
    .map((resource) => ({ resource, score: appleNameScore(resource, query) }))
    .sort((left, right) => right.score - left.score);

  return matched[0]?.resource || null;
};

export const formatAppleMusicArtworkUrl = (template?: string, size = 600) => {
  if (!template) return undefined;
  return template.replace("{w}", String(size)).replace("{h}", String(size));
};

const appleResourceToEnrichment = (
  resource: AppleMusicResource,
  query: AppleMusicEnrichmentQuery
): AppleMusicEnrichment | null => {
  const url = resource.attributes?.url;
  if (!url) return null;

  return {
    key: getAppleMusicEnrichmentKey(query),
    source: "apple-music",
    type: query.lookupType,
    url,
    name: resource.attributes?.name,
    artistName: resource.attributes?.artistName,
    albumName: resource.attributes?.albumName,
    artworkUrl: formatAppleMusicArtworkUrl(resource.attributes?.artwork?.url),
    previewUrl: resource.attributes?.previews?.find((preview) => preview.url)?.url,
    releaseDate: resource.attributes?.releaseDate,
    resourceId: resource.id,
    raw: resource,
  };
};

const itunesReleaseNameScore = (track: ItunesTrack, query: AppleMusicEnrichmentQuery) => {
  const target = query.releaseName || query.albumName || "";
  return query.lookupType === "album"
    ? releaseTitleScore(target, track.collectionName, track.trackName)
    : releaseTitleScore(target, track.trackName, track.collectionName);
};

const scoreItunesTrack = (track: ItunesTrack, query: AppleMusicEnrichmentQuery) => {
  let score = itunesReleaseNameScore(track, query);

  if (query.artistName && isArtistMatch(track.artistName, query.artistName)) score += 70;
  if (query.lookupType === "album" && track.wrapperType === "collection") score += 80;
  if (query.lookupType === "song" && track.wrapperType === "track") score += 80;
  if (track.trackViewUrl || track.collectionViewUrl) score += 10;

  return score;
};

const itunesArtistNameScore = (track: ItunesTrack, query: AppleMusicEnrichmentQuery) => {
  const target = normalizeAppleMusicKey(query.artistName || "");
  const artistName = normalizeAppleMusicKey(track.artistName || "");
  let score = 0;

  if (target && artistName === target) score += 140;
  if (target && artistName.length > 3 && (artistName.includes(target) || target.includes(artistName))) score += 24;

  return score;
};

export const selectItunesEnrichment = (
  data: ItunesData | undefined,
  query: AppleMusicEnrichmentQuery
): AppleMusicEnrichment | null => {
  const tracks = data?.tracks || [];
  if (tracks.length === 0) return null;

  const track = [...tracks]
    .filter((item) => query.lookupType === "artist" ? Boolean(item.artistLinkUrl) : Boolean(item.trackViewUrl || item.collectionViewUrl))
    .filter((item) => query.lookupType === "artist" ? itunesArtistNameScore(item, query) > 0 : itunesReleaseNameScore(item, query) > 0)
    .sort((left, right) => {
      if (query.lookupType === "artist") return itunesArtistNameScore(right, query) - itunesArtistNameScore(left, query);
      return scoreItunesTrack(right, query) - scoreItunesTrack(left, query);
    })[0];

  if (!track) return null;
  const url = query.lookupType === "artist"
    ? track.artistLinkUrl
    : query.lookupType === "album"
    ? track.collectionViewUrl || track.trackViewUrl
    : track.trackViewUrl || track.collectionViewUrl;

  if (!url) return null;

  return {
    key: getAppleMusicEnrichmentKey(query),
    source: "itunes",
    type: query.lookupType,
    url,
    name: query.lookupType === "artist" ? track.artistName : query.lookupType === "album" ? track.collectionName || track.trackName : track.trackName || track.collectionName,
    artistName: track.artistName,
    albumName: track.collectionName,
    artworkUrl: track.artworkUrl,
    previewUrl: track.previewUrl,
    releaseDate: track.releaseDate,
    resourceId: String(track.artistId || track.trackId || track.collectionId || ""),
    raw: track,
  };
};

export const createAppleMusicCatalogPayload = (query: AppleMusicEnrichmentQuery, appleLimit = 10) => {
  const releaseName = query.releaseName || query.albumName || query.artistName || "";
  const payload: Record<string, unknown> = {
    artistName: query.artistName,
    types: query.lookupType === "artist" ? "artists" : query.lookupType === "album" ? "albums" : "songs",
    limit: appleLimit,
  };

  if (query.lookupType === "artist") payload.term = query.artistName;
  if (query.lookupType === "album") payload.albumName = releaseName;
  if (query.lookupType === "song") payload.trackName = releaseName;

  return payload;
};

export const createItunesLookupUrl = (query: AppleMusicEnrichmentQuery, itunesLimit = 5) => {
  const params = new URLSearchParams({
    artistName: query.artistName || "",
    limit: String(itunesLimit),
    entity: query.lookupType === "artist" ? "artist" : query.lookupType === "album" ? "album" : "song",
  });
  const releaseName = query.releaseName || query.albumName;
  if (releaseName && query.lookupType !== "artist") params.set("trackName", releaseName);
  return `/.netlify/functions/GetItunesData?${params.toString()}`;
};

export const fetchAppleMusicEnrichment = async (
  query: AppleMusicEnrichmentQuery,
  options: FetchAppleMusicEnrichmentOptions = {}
): Promise<AppleMusicEnrichment | null> => {
  const artistName = query.artistName?.trim();
  const releaseName = (query.releaseName || query.albumName || query.artistName || "").trim();
  if (!artistName || !releaseName) return null;

  const normalizedQuery = { ...query, artistName, releaseName };
  const key = getAppleMusicEnrichmentKey(normalizedQuery);
  const ttlMs = options.ttlMs || API_CACHE_TTL.SIX_HOURS;
  const appleLimit = options.appleLimit || 10;
  const itunesLimit = options.itunesLimit || 5;

  try {
    const response = await fetchCachedGetApi<AppleMusicSearchResponse>("/.netlify/functions/AppleMusicCatalogSearch", createAppleMusicCatalogPayload(normalizedQuery, appleLimit), {
      cacheKey: `apple-music-enrichment:catalog:v1:${key}`,
      ttlMs,
      signal: options.signal,
      shouldCache: hasAppleMusicResults,
    });
    const resource = selectAppleMusicResource(response, normalizedQuery);
    const enrichment = resource ? appleResourceToEnrichment(resource, normalizedQuery) : null;
    if (enrichment && (!options.requirePreview || enrichment.previewUrl)) return enrichment;
  } catch (error) {
    void error;
  }

  try {
    const response = await fetchCachedApi<ItunesData>(createItunesLookupUrl(normalizedQuery, itunesLimit), {}, {
      cacheKey: `apple-music-enrichment:itunes:v1:${key}`,
      ttlMs,
      signal: options.signal,
      shouldCache: hasItunesResults,
    });
    return selectItunesEnrichment(response, normalizedQuery);
  } catch {
    return null;
  }
};
